import React from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api, SITE_URL } from "@/lib/api";
import { orgGraph, providerRef, webPage } from "@/lib/schema";
import { useCompany } from "@/context/CompanyContext";
import Seo from "@/components/Seo";
import Breadcrumbs from "@/components/Breadcrumbs";
import FaqAccordion from "@/components/FaqAccordion";
import LeadForm from "@/components/LeadForm";
import CtaBlock from "@/components/CtaBlock";
import NotFound from "@/pages/NotFound";
import { optimizeImg } from "@/lib/img";
import { MapPin, Phone, AlertTriangle, ArrowRight, CheckCircle2, Sparkles, ListChecks, Building2 } from "lucide-react";
import { trackEvent } from "@/lib/ga";

const CORE = ["ac-repair", "ac-installation", "ac-maintenance", "heating-repair", "heat-pumps", "indoor-air-quality"];

export default function ServiceLocationPage() {
  const { serviceSlug, citySlug } = useParams();
  const { company } = useCompany();
  const { data: service, isLoading: sLoad, isError: sErr } = useQuery({ queryKey: ["svc-combo", serviceSlug], queryFn: async () => (await api.get(`/services/${serviceSlug}`)).data, retry: false });
  const { data: loc, isLoading: lLoad, isError: lErr } = useQuery({ queryKey: ["loc-combo", citySlug], queryFn: async () => (await api.get(`/locations/${citySlug}`)).data, retry: false });
  const { data: allServices = [] } = useQuery({ queryKey: ["all-services-combo"], queryFn: async () => (await api.get("/services")).data });
  const { data: allLocations = [] } = useQuery({ queryKey: ["all-locations-combo"], queryFn: async () => (await api.get("/locations")).data });

  if (sLoad || lLoad) return <div className="max-w-7xl mx-auto px-4 py-24 text-slate-400" data-testid="combo-loading">Loading…</div>;
  if (sErr || lErr || !service || !loc) return <NotFound />;

  const otherServices = CORE.map((s) => allServices.find((x) => x.slug === s)).filter(Boolean).filter((s) => s.slug !== service.slug).slice(0, 5);
  const nearby = allLocations.filter((l) => l.slug !== loc.slug).slice(0, 6);
  const path = `/${service.slug}/${loc.slug}/`;
  const title = `${service.name} in ${loc.city}, FL | ${company.name || "Aquila Air & Heating"}`;
  const meta = `Expert ${service.name.toLowerCase()} in ${loc.city}, FL from ${company.name || "Aquila Air & Heating"}. Local Space Coast techs, upfront pricing, and 24/7 emergency service. Call today.`;
  const faqs = [...(service.faqs || []), ...(loc.faqs || [])];

  const jsonLd = [
    ...orgGraph(company),
    webPage(path, title, meta),
    {
      "@context": "https://schema.org", "@type": "Service",
      name: `${service.name} in ${loc.city}, FL`, serviceType: service.name,
      description: meta,
      provider: providerRef(),
      areaServed: { "@type": "City", name: `${loc.city}, ${loc.state}` },
      url: `${SITE_URL}${path}`,
    },
  ];

  return (
    <>
      <Seo title={title} description={meta} path={path} image={service.image} jsonLd={jsonLd} />

      <section className="relative bg-navy text-white" data-testid="combo-hero">
        <div className="absolute inset-0"><img src={optimizeImg(service.image, 1600)} alt={`${service.name} in ${loc.city}, FL`} className="w-full h-full object-cover opacity-30" loading="eager" decoding="async" width="1600" height="900" /><div className="absolute inset-0 bg-gradient-to-r from-navy via-navy/95 to-navy/70" /></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-16">
          <div className="[&_*]:!text-slate-300"><Breadcrumbs items={[{ label: "Home", to: "/" }, { label: service.name, to: `/${service.slug}` }, { label: `${loc.city}, FL` }]} /></div>
          <p className="mt-6 inline-flex items-center gap-2 text-sm font-bold uppercase tracking-[0.15em] text-launch"><MapPin className="h-4 w-4" /> {loc.city} · {loc.county}</p>
          <h1 className="mt-3 text-4xl sm:text-5xl font-extrabold tracking-tight" data-testid="combo-h1">{service.name} in {loc.city}, FL</h1>
          <p className="mt-4 max-w-2xl text-lg text-slate-200 leading-relaxed">{service.intro} Proudly serving {loc.city} and all of {loc.county}.</p>
          <a href={`tel:${company.phone_href || ""}`} onClick={() => trackEvent("phone_click", { location: "combo_hero", service: service.slug, city: loc.slug })} data-testid="combo-call" className="mt-7 inline-flex items-center gap-2 bg-launch hover:bg-launch-dark text-white font-bold py-3 px-7 rounded-md transition-colors"><Phone className="h-5 w-5" /> Call {company.phone}</a>
        </div>
      </section>

      <section className="py-14 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-3 gap-12">
          <div className="lg:col-span-2 space-y-10">
            <div className="bg-white border border-slate-200 rounded-xl p-6 sm:p-8">
              <h2 className="flex items-center gap-2 text-2xl font-bold text-navy"><MapPin className="h-6 w-6 text-cool" /> Why {loc.city} Homes Need {service.name}</h2>
              <p className="mt-3 text-slate-700 leading-relaxed">{loc.climate}</p>
              <div className="mt-4 grid sm:grid-cols-2 gap-3">
                {(loc.common_problems || []).slice(0, 4).map((pb) => (
                  <div key={pb} className="flex items-start gap-2 bg-slate-50 border border-slate-200 rounded-md p-4 text-sm text-slate-700"><AlertTriangle className="h-4 w-4 text-launch mt-0.5 flex-shrink-0" /> {pb}</div>
                ))}
              </div>
            </div>

            <div>
              <p className="text-lg leading-relaxed text-slate-700">{service.explanation}</p>
            </div>

            <div>
              <h2 className="flex items-center gap-2 text-2xl font-bold text-navy mb-4"><AlertTriangle className="h-6 w-6 text-launch" /> Signs You May Need {service.name}</h2>
              <div className="grid sm:grid-cols-2 gap-3">
                {(service.signs || []).map((s) => <div key={s} className="flex items-start gap-2 bg-slate-50 border border-slate-200 rounded-md p-4 text-sm text-slate-700"><CheckCircle2 className="h-4 w-4 text-launch mt-0.5 flex-shrink-0" /> {s}</div>)}
              </div>
            </div>

            <div>
              <h2 className="flex items-center gap-2 text-2xl font-bold text-navy mb-4"><Sparkles className="h-6 w-6 text-cool" /> The {loc.city} Homeowner's Advantage</h2>
              <div className="grid sm:grid-cols-2 gap-3">
                {(service.benefits || []).map((b) => <div key={b} className="flex items-start gap-2 bg-slate-50 border border-slate-200 rounded-md p-4 text-sm text-slate-700"><CheckCircle2 className="h-4 w-4 text-cool mt-0.5 flex-shrink-0" /> {b}</div>)}
              </div>
            </div>

            <div>
              <h2 className="flex items-center gap-2 text-2xl font-bold text-navy mb-4"><ListChecks className="h-6 w-6 text-navy" /> Our Process</h2>
              <ol className="space-y-2 list-decimal pl-5 text-slate-700">
                {(service.process || []).map((p) => <li key={p} className="pl-1">{p}</li>)}
              </ol>
            </div>

            {loc.neighborhoods?.length > 0 && (
              <div className="bg-white border border-slate-200 rounded-xl p-6 sm:p-8">
                <h2 className="flex items-center gap-2 text-2xl font-bold text-navy mb-3"><Building2 className="h-6 w-6 text-cool" /> {loc.city} Neighborhoods We Serve</h2>
                <div className="flex flex-wrap gap-2">
                  {loc.neighborhoods.map((n) => <span key={n} className="bg-cool/10 text-navy text-sm font-medium rounded-full px-3 py-1">{n}</span>)}
                </div>
              </div>
            )}

            {otherServices.length > 0 && (
              <div>
                <h2 className="text-2xl font-bold text-navy mb-4">More HVAC Services in {loc.city}</h2>
                <div className="grid sm:grid-cols-2 gap-4">
                  {otherServices.map((s) => (
                    <Link key={s.slug} to={`/${s.slug}/${loc.slug}`} data-testid={`combo-other-service-${s.slug}`} className="group flex items-center justify-between bg-white border border-slate-200 rounded-lg p-4 hover:border-cool transition-colors">
                      <span className="font-semibold text-navy">{s.name} in {loc.city}</span>
                      <ArrowRight className="h-4 w-4 text-launch group-hover:translate-x-1 transition-transform" />
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>

          <aside className="space-y-6">
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 lg:sticky lg:top-24">
              <h3 className="text-xl font-bold text-navy">{service.name} in {loc.city}?</h3>
              <p className="text-sm text-slate-600 mt-1 mb-4">Local, fast, and honest.</p>
              <LeadForm defaultService={service.name} compact />
            </div>
            {nearby.length > 0 && (
              <div className="bg-white rounded-xl border border-slate-200 p-6">
                <h3 className="text-lg font-semibold text-navy mb-3">{service.name} Nearby</h3>
                <ul className="space-y-2">
                  {nearby.map((n) => <li key={n.slug}><Link to={`/${service.slug}/${n.slug}`} className="flex items-center gap-2 text-sm text-slate-700 hover:text-launch transition-colors" data-testid={`combo-nearby-${n.slug}`}><MapPin className="h-4 w-4 text-launch" /> {service.name} in {n.city}</Link></li>)}
                </ul>
              </div>
            )}
          </aside>
        </div>
      </section>

      {faqs.length > 0 && <div className="bg-slate-100"><FaqAccordion faqs={faqs} title={`${service.name} in ${loc.city} — FAQs`} /></div>}
      <CtaBlock heading={`${service.name} in ${loc.city}? We're Ready for Liftoff.`} />
    </>
  );
}
